import { useCallback, useEffect, useState } from 'react';

/** Rolling windows plus an explicit "everything" escape hatch. */
export const DATE_RANGES = [
  { value: '1', label: 'Last 24 hours' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
] as const;

export function rangeToSince(range: string): string | undefined {
  if (range === 'all') return undefined;
  const days = Number(range);
  if (!Number.isFinite(days) || days <= 0) return undefined;
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

/**
 * Filter state mirrored into the URL.
 *
 * A filtered log view is something operators share ("look at this failure") and
 * come back to, so the filters belong in the address bar rather than in
 * component state that a refresh discards. Keys are namespaced by `prefix` so
 * two viewers on one page (Activity and AI Logs) never overwrite each other.
 *
 * Nothing written here can widen a portal's scope: the viewer merges its scope
 * over these values, and the Worker merges it again over whatever arrives. A
 * hand-edited URL parameter reaches the query builder already overwritten.
 */
export function useLogFilters(prefix: string, defaultRange = '30') {
  const read = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const values: Record<string, string | undefined> = {};
    for (const [key, value] of params.entries()) {
      if (key.startsWith(`${prefix}.`) && value) values[key.slice(prefix.length + 1)] = value;
    }
    const range = values.range ?? defaultRange;
    delete values.range;
    return { values, range };
  }, [prefix, defaultRange]);

  const [state, setState] = useState(read);

  // Keep the viewer in step with browser navigation.
  useEffect(() => {
    const onPop = () => setState(read());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [read]);

  const write = useCallback(
    (values: Record<string, string | undefined>, range: string) => {
      const params = new URLSearchParams(window.location.search);
      for (const key of [...params.keys()]) {
        if (key.startsWith(`${prefix}.`)) params.delete(key);
      }
      for (const [key, value] of Object.entries(values)) {
        if (value) params.set(`${prefix}.${key}`, value);
      }
      if (range !== defaultRange) params.set(`${prefix}.range`, range);
      const qs = params.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${qs ? `?${qs}` : ''}`,
      );
      setState({ values, range });
    },
    [prefix, defaultRange],
  );

  const setValue = useCallback(
    (key: string, value: string | undefined) => {
      const next = { ...state.values };
      if (value) next[key] = value;
      else delete next[key];
      write(next, state.range);
    },
    [state, write],
  );

  const setRange = useCallback((range: string) => write(state.values, range), [state, write]);
  const clear = useCallback(() => write({}, state.range), [state.range, write]);

  return { values: state.values, range: state.range, setValue, setRange, clear };
}

/**
 * Build a React Query key for a log viewer.
 *
 * React Query hashes the key by content, so any clock-derived value inside it
 * produces a different key on every render — which fetches, re-renders, and
 * fetches again forever. `since`/`until` are resolved from `range` at fetch
 * time and are stripped here so they can never reach the key by accident. The
 * `range` token stands in for them: it changes when the operator changes the
 * window and at no other time.
 */
export function logQueryKey(prefix: string, params: object, range: string): unknown[] {
  const { since: _since, until: _until, cursor: _cursor, ...stable } =
    params as Record<string, unknown>;
  return [prefix, stable, range];
}
